/**
 * Service-scoped WAAP hooks. WAAP is a standalone product (the Cobra service)
 * with its own domain binding namespace (type='waap'), fully separate from the
 * CDN service views — so the WAAP console sources its website list HERE, never
 * from /services/:id/cdn/domains (which lists type='cdn' bindings and is empty
 * for a WAAP service).
 *   GET /api/v3/services/:id/waap/domains
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { type Envelope, type DomainListPagination } from './_shared';
import { type CDNDomain, type DomainListQuery } from './domains';

export interface ServiceWaapDomainList {
  data: CDNDomain[];
  pagination: DomainListPagination;
}

/** Server-paginated WAAP domain (website) list scoped to one WAAP service.
 *  Same return shape as useServiceCdnDomainsPaged so callers swap transparently. */
export function useServiceWaapDomainsPaged(serviceId: string, q: DomainListQuery) {
  return useQuery({
    queryKey: ['waap', 'service', serviceId, 'domains', 'paged', q.page, q.perPage, q.search ?? ''],
    enabled: !!serviceId && (q.enabled ?? true),
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: async (): Promise<ServiceWaapDomainList> => {
      const params = new URLSearchParams();
      params.set('page', String(q.page));
      params.set('per_page', String(q.perPage));
      if (q.search) params.set('search', q.search);
      const res = await serviceApiV3.get<Envelope<CDNDomain[]>>(
        `/services/${serviceId}/waap/domains?${params.toString()}`,
      );
      return {
        data: res.data?.data ?? [],
        pagination: res.data?.pagination ?? { page: q.page, per_page: q.perPage, total: 0 },
      };
    },
  });
}

export interface DeleteWaapServiceResult {
  service_name?: string;
}

/** Delete a WAAP service (removes the service row + its provisioned zones).
 *  DELETE /api/v3/services/:id — the generic service-delete endpoint. */
export function useDeleteWaapService(
  serviceId: string,
  options?: UseMutationOptions<DeleteWaapServiceResult, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<DeleteWaapServiceResult> => {
      const res = await serviceApiV3.delete<{ data?: DeleteWaapServiceResult }>(
        `/services/${serviceId}`,
      );
      return res.data?.data ?? {};
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['my-services'] });
      options?.onSuccess?.(...args);
    },
  });
}
